import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const required = z.string().trim().min(1);

// Kept as a literal tuple (not derived from SENSOR_TYPES) so zod infers the
// `SensorType` literal union instead of widening to `string`.
export const sensorTypeDtoSchema = z.enum([
  "WEIGHT",
  "TEMPERATURE",
  "DOOR_STATUS",
  "IBUTTON",
  "PTO",
  "SEAT_BELT",
  "REFRIGERATOR_STATE",
  "HEADLIGHTS",
  "SOS",
  "RFID",
]);

// Kept as its own literal tuple for the same reason as sensorTypeDtoSchema.
// DOOR_STATUS, PTO, SEAT_BELT, REFRIGERATOR_STATE, HEADLIGHTS and SOS were
// all pulled out once each was confirmed (2026-09-23) to actually match a
// richer, shared shape -- see valueMapSensorTypeDtoSchema/valueMapConfigDtoSchema.
export const simpleIoSensorTypeDtoSchema = z.enum(["IBUTTON"]);

// Kept as its own literal tuple for the same reason as sensorTypeDtoSchema.
export const valueMapSensorTypeDtoSchema = z.enum([
  "DOOR_STATUS",
  "PTO",
  "SEAT_BELT",
  "REFRIGERATOR_STATE",
  "HEADLIGHTS",
  "SOS",
]);

export const weightFormulaTypeDtoSchema = z.enum(["NONE", "SUM", "PERCENT"]);

// `GET /sensors` -- Sensors Management list.
export const sensorDeviceRefDtoSchema = z.object({
  id: required,
  code: required,
  imei: required,
});

export const sensorAccountRefDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
});

export const sensorConfigRefDtoSchema = z.object({
  id: required,
  deviceCount: z.number().int().min(0),
});

export const sensorAssignmentListItemDtoSchema = z.object({
  id: required,
  sensorType: sensorTypeDtoSchema,
  name: required,
  configType: weightFormulaTypeDtoSchema.nullable(),
  minValue: z.number().nullable(),
  maxValue: z.number().nullable(),
  unit: z.string().nullable(),
  tagLength: z.number().int().nullable(),
  sensorConfig: sensorConfigRefDtoSchema,
  device: sensorDeviceRefDtoSchema,
  account: sensorAccountRefDtoSchema,
  createdAt: required,
});

// `WEIGHT`'s `config` shape -- the only sensor type this phase builds a
// step-2 form for. See WeightConfig in ../domain/models.
export const weightCalibrationPointDtoSchema = z.object({
  mv: z.number(),
  kg: z.number(),
});

export const weightConfigIoDtoSchema = z.object({
  io: z.number().int().min(0).max(65535),
  label: required.optional(),
  minWeightKGs: z.number().min(0),
  maxWeightKGs: z.number(),
  calibration: z.array(weightCalibrationPointDtoSchema),
  ioFormulaPercentage: z.number().min(0).max(100).nullable(),
});

export const weightConfigDtoSchema = z.object({
  label: required.max(100),
  unitLabel: required,
  formulaType: weightFormulaTypeDtoSchema,
  ios: z.array(weightConfigIoDtoSchema),
});

// `TEMPERATURE`'s `config` shape. See TemperatureConfig in ../domain/models.
export const temperatureProbeTypeDtoSchema = z.enum(["BLE", "DALLAS"]);
export const temperatureBeaconModelDtoSchema = z.enum([
  "TZONE_BT04",
  "MINEW_MST01",
]);

export const temperatureChannelDtoSchema = z.object({
  io: z.number().int().min(0).max(65535),
  unitLabel: z.string().optional(),
});

export const temperatureConfigIoDtoSchema = z.object({
  label: required,
  probeType: temperatureProbeTypeDtoSchema,
  beaconModel: temperatureBeaconModelDtoSchema,
  temperature: temperatureChannelDtoSchema,
  humidity: temperatureChannelDtoSchema.optional(),
  battery: temperatureChannelDtoSchema.optional(),
  dallasId: z.object({ io: z.number().int().min(0).max(65535) }).optional(),
});

export const temperatureConfigDtoSchema = z.object({
  label: required.max(100),
  ios: z.array(temperatureConfigIoDtoSchema),
});

// The seven SimpleIoSensorType configs -- a plain list of IO numbers.
export const simpleIoConfigRowDtoSchema = z.object({
  io: z.number().int().min(0).max(65535),
});

export const simpleIoConfigDtoSchema = z.object({
  label: required.max(100),
  ios: z.array(simpleIoConfigRowDtoSchema).min(1),
});

// RFID's config -- an IO number plus the tag's verification-mark length.
// tagLength's 1..64 bound is confirmed with BE (2026-09-24).
export const rfidConfigRowDtoSchema = z.object({
  io: z.number().int().min(0).max(65535),
  tagLength: z.number().int().min(1).max(64),
});

export const rfidConfigDtoSchema = z.object({
  label: required.max(100),
  ios: z.array(rfidConfigRowDtoSchema).min(1),
});

// The ValueMapSensorType config shape (DOOR_STATUS, PTO, SEAT_BELT,
// REFRIGERATOR_STATE, HEADLIGHTS, SOS) -- confirmed real (2026-09-23,
// GET /sensors/form?sensorType=...), identical across all six. Each `ios[]`
// entry needs its own label plus exactly two value mappings (`groups: [{
// key: "ios[].values[]", minItems: 2, maxItems: 2 }]` in the live response
// -- not modeled as a schema constraint since ValueMapSensorConfigStep
// hardcodes the two rows itself rather than reading `groups` generically).
export const valueMapConfigValueDtoSchema = z.object({
  value: z.number(),
  label: z.object({ en: required, ar: required }),
});

export const valueMapConfigRowDtoSchema = z.object({
  label: required.max(100),
  io: z.number().int().min(0).max(65535),
  values: z.array(valueMapConfigValueDtoSchema),
});

export const valueMapConfigDtoSchema = z.object({
  label: required.max(100),
  ios: z.array(valueMapConfigRowDtoSchema).min(1),
});

// `GET /sensors/:sensorId` (and `GET /devices/:deviceId/sensors`) --
// confirmed real (2026-09-22): one sensor with its full configuration.
// `config`'s shape is only disambiguated by the sibling `sensorType` field.
//
// NOT the same shape `GET /sensors` (the list) returns, despite both being
// documented with a `config`/`updatedAt`-bearing example: the live list
// endpoint's actual rows don't carry either field (confirmed 2026-09-22
// against a real response) -- see sensorAssignmentsListResponseDtoSchema.
//
// `config` is intentionally left unvalidated here (`z.unknown()`, not the
// WEIGHT/TEMPERATURE union): a real fleet mixes every sensor type, and the
// other 6 (DOOR_STATUS, IBUTTON, PTO, SEAT_BELT, REFRIGERATOR_STATE,
// HEADLIGHTS, SOS, RFID) have real, non-null config objects on the wire that
// this app has no contract for yet. Validating against a 2-shape union rejects every one of
// those rows and fails the *entire* array parse (z.array is all-or-nothing)
// even though the request itself succeeded. sensorConfigFromDto already
// only interprets `config` when `sensorType` is WEIGHT/TEMPERATURE and
// returns `null` otherwise, so the schema doesn't need to gate it too.
export const sensorDetailDtoSchema = sensorAssignmentListItemDtoSchema.extend({
  config: z.unknown().nullable(),
  updatedAt: required,
});

export const sensorDetailResponseDtoSchema = z.object({
  data: sensorDetailDtoSchema,
  message: z.string().optional(),
});

// `GET /sensors` -- confirmed real (2026-09-22). Rows are the base shape
// only (no `config`/`updatedAt`) -- see sensorAssignmentListItemDtoSchema
// and the comment on sensorDetailDtoSchema above.
export const sensorAssignmentsListResponseDtoSchema = z.object({
  data: z.array(sensorAssignmentListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

// `GET /devices/:deviceId/sensors` -- confirmed real (2026-09-22): every
// sensor on one device, same row shape as `GET /sensors/:sensorId`. Backs
// the "copy from device" picker's device -> candidate sensor lookup.
export const sensorDeviceSensorsResponseDtoSchema = z.object({
  data: z.array(sensorDetailDtoSchema),
});

// `GET /sensors/form?sensorType=` -- validation bounds/options only, never
// layout. See SensorFormMeta.
export const sensorFormOptionDtoSchema = z.object({
  value: required,
  labelEn: required,
  labelAr: required,
  sortOrder: z.number().int(),
});

export const sensorFormFieldValidationDtoSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  pattern: z.string().optional(),
  patternFlags: z.string().optional(),
});

export const sensorFormFieldDtoSchema = z.object({
  key: required,
  type: z.enum(["INT", "FLOAT", "DECIMAL", "STRING", "SELECT", "BOOLEAN"]),
  labelEn: required,
  labelAr: required,
  requiredIn: z.array(z.string()),
  forbiddenIn: z.array(z.string()),
  optionGroup: z.string().optional(),
  validation: sensorFormFieldValidationDtoSchema,
  sortOrder: z.number().int(),
});

export const sensorFormMetaDtoSchema = z.object({
  sensorType: sensorTypeDtoSchema,
  variantKey: z.string().nullable(),
  variants: z.array(z.string()),
  options: z.record(z.string(), z.array(sensorFormOptionDtoSchema)),
  fields: z.array(sensorFormFieldDtoSchema),
  rules: z.array(z.string()),
});

export const sensorFormResponseDtoSchema = z.object({
  data: sensorFormMetaDtoSchema,
  message: z.string().optional(),
});

// `POST /sensors` -- confirmed real (2026-09-22): the body is flat
// `deviceIds: string[]` plus either `sensorType` + `config` or
// `sensorConfigId`, never both/neither. No "all"/branch concept exists on
// the wire -- see SensorTargetScope for the PLANNED extension this is
// standing in for.
export const sensorCreateInputDtoSchema = z.union([
  z.object({
    deviceIds: z.array(required).min(1),
    sensorType: z.literal("WEIGHT"),
    config: weightConfigDtoSchema,
  }),
  z.object({
    deviceIds: z.array(required).min(1),
    sensorType: z.literal("TEMPERATURE"),
    config: temperatureConfigDtoSchema,
  }),
  z.object({
    deviceIds: z.array(required).min(1),
    sensorType: simpleIoSensorTypeDtoSchema,
    config: simpleIoConfigDtoSchema,
  }),
  z.object({
    deviceIds: z.array(required).min(1),
    sensorType: z.literal("RFID"),
    config: rfidConfigDtoSchema,
  }),
  z.object({
    deviceIds: z.array(required).min(1),
    sensorType: valueMapSensorTypeDtoSchema,
    config: valueMapConfigDtoSchema,
  }),
  z.object({
    deviceIds: z.array(required).min(1),
    sensorConfigId: required,
  }),
]);

// `PUT /sensors/:sensorId` -- config only; sensorType/device are immutable.
// Kept as a union purely for `SensorUpdateInputDto`'s inferred type -- never
// used to validate an outgoing request directly (see
// sensorUpdateInputDtoSchemaFor and why: the wire body carries no `sensorType`
// to discriminate on, and these config shapes aren't disjoint -- every field
// simpleIoConfigDtoSchema declares is also present in rfidConfigDtoSchema and
// valueMapConfigDtoSchema. `z.union` returns the *first* schema that parses, and a
// non-strict `z.object` silently strips fields it doesn't declare rather than
// failing, so validating an RFID or value-mapped body against this union would
// match simpleIoConfigDtoSchema first and silently drop tagLength/values from
// what gets sent.
export const sensorUpdateInputDtoSchema = z.object({
  config: z.union([
    weightConfigDtoSchema,
    temperatureConfigDtoSchema,
    simpleIoConfigDtoSchema,
    rfidConfigDtoSchema,
    valueMapConfigDtoSchema,
  ]),
});

/** The one config schema for `sensorType` -- see sensorUpdateInputDtoSchema's comment. */
export function sensorUpdateInputDtoSchemaFor(sensorType: string) {
  const configSchema =
    sensorType === "WEIGHT"
      ? weightConfigDtoSchema
      : sensorType === "TEMPERATURE"
        ? temperatureConfigDtoSchema
        : sensorType === "RFID"
          ? rfidConfigDtoSchema
          : (valueMapSensorTypeDtoSchema.options as readonly string[]).includes(sensorType)
            ? valueMapConfigDtoSchema
            : simpleIoConfigDtoSchema;
  return z.object({ config: configSchema });
}

export const sensorCreateResultDataDtoSchema = z.object({
  sensorConfigId: required,
  created: z.array(sensorDetailDtoSchema),
  updated: z.array(sensorDetailDtoSchema),
});

export const sensorCreateResponseDtoSchema = z.object({
  data: sensorCreateResultDataDtoSchema,
  message: z.string().optional(),
});

export const sensorUpdateResponseDtoSchema = z.object({
  data: sensorDetailDtoSchema,
  message: z.string().optional(),
});

// `POST /sensors` 409 -- IO already assigned. Same shape `ARCHIVED_DEVICE_FOUND`
// uses on `POST /devices`, disambiguated by the `code` string this time since
// (unlike the archived-device case) a 409 on this endpoint has only one shape.
export const sensorIoConflictEntryDtoSchema = z.object({
  deviceId: required,
  imei: required,
  io: z.number().int(),
});

export const sensorIoConflictResponseDtoSchema = z.object({
  code: z.literal("SENSOR_IO_ALREADY_ASSIGNED"),
  message: required,
  data: z.array(sensorIoConflictEntryDtoSchema),
});

// `DELETE /sensors/:sensorId` -- confirmed real (2026-09-22).
export const sensorDeleteResponseDtoSchema = z.object({
  message: required,
});

export type SensorAssignmentListItemDto = z.infer<
  typeof sensorAssignmentListItemDtoSchema
>;
export type SensorDetailDto = z.infer<typeof sensorDetailDtoSchema>;
export type WeightConfigDto = z.infer<typeof weightConfigDtoSchema>;
export type TemperatureConfigDto = z.infer<typeof temperatureConfigDtoSchema>;
export type SimpleIoConfigDto = z.infer<typeof simpleIoConfigDtoSchema>;
export type RfidConfigDto = z.infer<typeof rfidConfigDtoSchema>;
export type ValueMapConfigDto = z.infer<typeof valueMapConfigDtoSchema>;
export type SensorFormMetaDto = z.infer<typeof sensorFormMetaDtoSchema>;
export type SensorCreateInputDto = z.infer<typeof sensorCreateInputDtoSchema>;
export type SensorUpdateInputDto = z.infer<typeof sensorUpdateInputDtoSchema>;
export type SensorCreateResultDataDto = z.infer<
  typeof sensorCreateResultDataDtoSchema
>;
export type SensorIoConflictEntryDto = z.infer<
  typeof sensorIoConflictEntryDtoSchema
>;
